/**
 * Dataverse-based tools for solutions, canvas apps, AI models, etc.
 */

import { z } from "zod";
import type { DataverseApi } from "../api/dataverse-api.js";
import { logger } from "../utils/logger.js";

// =============================================================================
// Tool Definitions
// =============================================================================

export const listSolutionsTool = {
  name: "list_solutions",
  description:
    "List Dataverse solutions in the environment. Solutions are containers for flows, apps, and other components. Use this to see what's deployed and manage solution-aware components.",
  inputSchema: {
    type: "object" as const,
    properties: {
      includeManaged: {
        type: "boolean",
        description: "Include managed (read-only) solutions. Default: false",
        default: false,
      },
    },
  },
};

export const listSolutionFlowsTool = {
  name: "list_solution_flows",
  description:
    "List flows stored in Dataverse solutions. These are 'solution-aware' flows that can be exported and deployed across environments. Includes version history access.",
  inputSchema: {
    type: "object" as const,
    properties: {
      solutionId: {
        type: "string",
        description: "Filter flows by solution ID (GUID). Optional; prefer solutionUniqueName.",
      },
      solutionUniqueName: {
        type: "string",
        description:
          "Filter flows by solution unique name — the same identifier every other solution tool takes. Resolved to its ID for you.",
      },
    },
  },
};

export const listCanvasAppsTool = {
  name: "list_canvas_apps",
  description:
    "List Power Apps canvas apps stored in Dataverse. Shows app names, versions, and status.",
  inputSchema: {
    type: "object" as const,
    properties: {},
  },
};

export const listAIModelsTool = {
  name: "list_ai_models",
  description:
    "List AI Builder models in the environment. Shows model names, types, and status.",
  inputSchema: {
    type: "object" as const,
    properties: {},
  },
};

export const listApprovalsDataverseTool = {
  name: "list_approvals_dataverse",
  description:
    "List pending approval requests from Dataverse. More reliable than the Flow API for approvals. Shows approval title, stage, and requester.",
  inputSchema: {
    type: "object" as const,
    properties: {},
  },
};

// =============================================================================
// Input Schemas
// =============================================================================

export const listSolutionsInputSchema = z.object({
  includeManaged: z.boolean().optional().default(false),
});

export const listSolutionFlowsInputSchema = z.object({
  solutionId: z.string().optional(),
  solutionUniqueName: z.string().min(1).max(128).optional(),
});

export const listCanvasAppsInputSchema = z.object({});

export const listAIModelsInputSchema = z.object({});

export const listApprovalsDataverseInputSchema = z.object({});

// =============================================================================
// Tool Handlers
// =============================================================================

export interface DataverseToolsContext {
  api: DataverseApi | null;
}

export async function handleListSolutions(
  context: DataverseToolsContext,
  input: unknown
): Promise<string | { text: string; structured?: Record<string, unknown> }> {
  if (!context.api) {
    return `Dataverse not available for this environment.

This typically means:
1. The environment doesn't have Dataverse provisioned
2. It's a "Default" environment without a Dataverse database

To use Dataverse features:
- Create a new environment with a Dataverse database in Power Platform admin center
- Or provision Dataverse in your existing environment

Note: Solutions, Canvas Apps, AI Builder, and Approvals require Dataverse.`;
  }

  const parsed = listSolutionsInputSchema.parse(input);
  logger.info({ includeManaged: parsed.includeManaged }, "Executing list_solutions");

  try {
    const solutions = await context.api.listSolutions(parsed.includeManaged);

    if (solutions.length === 0) {
      const text = parsed.includeManaged
        ? "No solutions found in this environment."
        : "No unmanaged solutions found. Use includeManaged: true to see all solutions.";
      return { text, structured: { count: 0, includeManaged: parsed.includeManaged, solutions: [] } };
    }

    const lines = [`Solutions (${solutions.length}):`];
    for (const sol of solutions) {
      const managed = sol.ismanaged ? " [Managed]" : "";
      lines.push(`  • ${sol.friendlyname}${managed}`);
      lines.push(`    ID: ${sol.solutionid}`);
      lines.push(`    Unique Name: ${sol.uniquename}`);
      lines.push(`    Version: ${sol.version}`);
      lines.push(`    Publisher: ${sol.publisherid?.friendlyname || "Unknown"}`);
      if (sol.installedon) {
        lines.push(`    Installed: ${new Date(sol.installedon).toLocaleDateString()}`);
      }
    }

    // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
    const structured: Record<string, unknown> = {
      count: solutions.length,
      includeManaged: parsed.includeManaged,
      solutions: solutions.map((sol) => ({
        id: sol.solutionid,
        uniqueName: sol.uniquename,
        displayName: sol.friendlyname,
        version: sol.version,
        isManaged: sol.ismanaged,
        publisher: sol.publisherid?.friendlyname ?? null,
        ...(sol.installedon ? { installedOn: sol.installedon } : {}),
      })),
    };

    return { text: lines.join("\n"), structured };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes("401") || msg.includes("403") || msg.includes("Authentication")) {
      return "Dataverse authentication failed. Ensure you have:\n1. Run Add-AllApiPermissions.ps1\n2. Granted admin consent\n3. Re-authenticated with: powerautomate-mcp --setup";
    }
    throw error;
  }
}

export async function handleListSolutionFlows(
  context: DataverseToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) {
    return `Dataverse not available for this environment.

This typically means:
1. The environment doesn't have Dataverse provisioned
2. It's a "Default" environment without a Dataverse database

To use solution flows:
- Create an environment with a Dataverse database in Power Platform admin center
- Or use list_flows to see non-solution flows in this environment`;
  }

  const parsed = listSolutionFlowsInputSchema.parse(input);
  logger.info(
    { solutionId: parsed.solutionId, solutionUniqueName: parsed.solutionUniqueName },
    "Executing list_solution_flows",
  );

  try {
    // Every other solution tool is keyed on the unique name; this one took a
    // GUID, which forced a lookup nothing else asks for.
    let solutionId = parsed.solutionId;
    if (solutionId === undefined && parsed.solutionUniqueName !== undefined) {
      const found = await context.api.queryRows("solutions", {
        select: "solutionid",
        filter: `uniquename eq '${parsed.solutionUniqueName.replace(/'/g, "''")}'`,
        top: 1,
      });
      const row = found.value[0];
      if (row === undefined) {
        return `No solution named '${parsed.solutionUniqueName}' exists in this environment.`;
      }
      solutionId = String(row["solutionid"]);
    }

    const flows = await context.api.listSolutionFlows(solutionId);

    if (flows.length === 0) {
      // Key on the RESOLVED id, not the raw parameter — a solution named by
      // unique name is still a filtered query, and saying otherwise would
      // report a scoped search as if it had covered the whole environment.
      return solutionId
        ? "No flows found in this solution."
        : "No solution-aware flows found. Non-solution flows are only accessible via the Flow API.";
    }

    const lines = [`Solution Flows (${flows.length}):`];
    for (const flow of flows) {
      const state = flow.statecode === 1 ? "▶" : "⏹";
      lines.push(`  ${state} ${flow.name}`);
      lines.push(`    ID: ${flow.workflowid}`);
      lines.push(`    Owner: ${flow.ownerid?.name || "Unknown"}`);
      lines.push(`    Modified: ${new Date(flow.modifiedon).toLocaleString()}`);
    }

    return lines.join("\n");
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes("401") || msg.includes("403")) {
      return "Dataverse authentication failed. Ensure permissions are configured correctly.";
    }
    throw error;
  }
}

export async function handleListCanvasApps(
  context: DataverseToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) {
    return `Dataverse not available for this environment.

This typically means:
1. The environment doesn't have Dataverse provisioned
2. It's a "Default" environment without a Dataverse database

Canvas apps stored in Dataverse require an environment with Dataverse.
Note: Some canvas apps may still be accessible via the PowerApps portal.`;
  }

  listCanvasAppsInputSchema.parse(input);
  logger.info("Executing list_canvas_apps");

  try {
    const apps = await context.api.listCanvasApps();

    if (apps.length === 0) {
      return "No canvas apps found in this environment.";
    }

    const lines = [`Canvas Apps (${apps.length}):`];
    for (const app of apps) {
      lines.push(`  • ${app.displayname || app.name}`);
      lines.push(`    ID: ${app.canvasappid}`);
      lines.push(`    Version: ${app.appversion || "N/A"}`);
      lines.push(`    Status: ${app.status || "Unknown"}`);
      if (app.lastmodifiedtime) {
        lines.push(`    Modified: ${new Date(app.lastmodifiedtime).toLocaleString()}`);
      }
      if (app.owner) {
        lines.push(`    Owner: ${app.owner}`);
      }
      if (app.description) {
        lines.push(`    Description: ${app.description.slice(0, 100)}${app.description.length > 100 ? "..." : ""}`);
      }
    }

    return lines.join("\n");
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes("401") || msg.includes("403")) {
      return "Dataverse authentication failed. Canvas apps require Dataverse permissions.";
    }
    if (msg.includes("404") || msg.includes("canvasapps")) {
      return "Canvas apps table not found. This environment may not have Power Apps deployed.";
    }
    throw error;
  }
}

export async function handleListAIModels(
  context: DataverseToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) {
    return `Dataverse not available for this environment.

This typically means:
1. The environment doesn't have Dataverse provisioned
2. It's a "Default" environment without a Dataverse database

AI Builder models require Dataverse. To use AI Builder:
- Create an environment with a Dataverse database in Power Platform admin center`;
  }

  listAIModelsInputSchema.parse(input);
  logger.info("Executing list_ai_models");

  try {
    const models = await context.api.listAIModels();

    if (models.length === 0) {
      return "No AI Builder models found in this environment.";
    }

    const lines = [`AI Builder Models (${models.length}):`];
    for (const model of models) {
      const status = model.statecode === 0 ? "Draft" : model.statecode === 1 ? "Active" : "Unknown";
      lines.push(`  • ${model.msdyn_name}`);
      lines.push(`    ID: ${model.msdyn_aimodelid}`);
      lines.push(`    Type: ${model.msdyn_modelcreationcontext || "Unknown"}`);
      lines.push(`    Status: ${status}`);
      lines.push(`    Modified: ${new Date(model.modifiedon).toLocaleString()}`);
    }

    return lines.join("\n");
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes("401") || msg.includes("403")) {
      return "Dataverse authentication failed. AI Builder requires Dataverse permissions.";
    }
    if (msg.includes("404") || msg.includes("msdyn_aimodels")) {
      return "AI Builder not available. This environment may not have AI Builder enabled.";
    }
    throw error;
  }
}

export async function handleListApprovalsDataverse(
  context: DataverseToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) {
    return `Dataverse not available for this environment.

This typically means:
1. The environment doesn't have Dataverse provisioned
2. It's a "Default" environment without a Dataverse database

The Approvals feature requires Dataverse. Approvals data is stored in:
- msdyn_flow_approvals table in Dataverse

To use approvals:
- Create an environment with a Dataverse database
- Or use the list_approvals tool (requires appropriate permissions)`;
  }

  listApprovalsDataverseInputSchema.parse(input);
  logger.info("Executing list_approvals_dataverse");

  try {
    const approvals = await context.api.listApprovalsDataverse();

    if (approvals.length === 0) {
      return "No pending approvals found.";
    }

    const lines = [`Pending Approvals (${approvals.length}):`];
    for (const approval of approvals) {
      lines.push(`  • ${approval.msdyn_flow_approval_name ?? "(unnamed)"}`);
      lines.push(`    ID: ${approval.msdyn_flow_approvalid}`);
      // Stage is a picklist; show its label when Dataverse supplied one
      // rather than a bare option number.
      lines.push(
        `    Stage: ${approval.msdyn_flow_approval_stage_label ?? approval.msdyn_flow_approval_stage ?? "unknown"}`,
      );
      lines.push(`    Requested by: ${approval.ownerid?.name || "Unknown"}`);
      if (approval.createdon) {
        lines.push(`    Requested: ${new Date(approval.createdon).toLocaleString()}`);
      }
      if (approval.msdyn_flow_approval_dueon) {
        lines.push(`    Due: ${new Date(approval.msdyn_flow_approval_dueon).toLocaleString()}`);
      }
      if (approval.msdyn_flow_approval_result) {
        lines.push(`    Result: ${approval.msdyn_flow_approval_result}`);
      }
    }

    return lines.join("\n");
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes("401") || msg.includes("403")) {
      return "Dataverse authentication failed. Approvals require Dataverse permissions.";
    }
    if (msg.includes("404") || msg.includes("msdyn_flow_approvals")) {
      return "Approvals table not found. Approvals may not be enabled in this environment.";
    }
    throw error;
  }
}
